import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { inventoryApi } from '../api/inventoryApi';
import { shippingApi } from '../api/shippingApi';
import { DetailDrawer } from '../components/common/DetailDrawer';
import { HacchuSplitPlanInlineEditor } from '../features/shipping/HacchuSplitPlanInlineEditor';
import { patchItemsSplitPlans } from '../features/shipping/splitPlanUtils';
import { ShipmentMemoBadges } from '../features/shipping/ShipmentMemoBadges';
import {
  getShipmentDateUnsetCellClassName,
  isShipmentDateUnset,
} from '../features/shipping/shipmentDateUtils';
import { ShipmentPlanDetailFields } from '../features/shipping/ShipmentPlanDetailFields';
import { formatShipmentPlanDate } from '../features/shipping/shipmentPlanFieldUtils';
import { useShipmentPlanFieldSave } from '../features/shipping/useShipmentPlanFieldSave';
import { PageSyncMeta } from '../components/sync/PageSyncMeta';
import { useSyncStatus } from '../contexts/SyncStatusContext';
import { formatDateTime } from '../utils/formatDateTime';
import {
  getShipmentRowClassName,
  getShipmentRowStage,
  SHIPMENT_ROW_LEGEND,
} from '../utils/getShipmentRowStage';

const InventoryDetailPage = () => {
  const { productDisp } = useParams();
  const [searchParams] = useSearchParams();
  const factory = searchParams.get('factory') || '26';

  const [summary, setSummary] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPurchaseOrders, setLoadingPurchaseOrders] = useState(false);
  const { refreshSyncStatus } = useSyncStatus();
  const [selectedItem, setSelectedItem] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set()); // 展開されている行のIDを保持
  const [shipmentDestinationTotalWeight, setShipmentDestinationTotalWeight] = useState(null); // 発送先総重量
  const [loadingTotalWeight, setLoadingTotalWeight] = useState(false); // 発送先総重量の読み込み中フラグ
  const inputRefs = useRef({});
  const [expandedSections, setExpandedSections] = useState({
    calendar: true,
    purchaseOrders: true,  // デフォルトで表示
  });
  const [shipmentDateFilter, setShipmentDateFilter] = useState('all'); // 'all', 'null', 'not_null'
  const [calendarViewType, setCalendarViewType] = useState('week'); // 'week' or 'month'
  const [calendarMonth, setCalendarMonth] = useState(new Date()); // 月カレンダーで表示する月
  const [stdStock, setStdStock] = useState(null);
  const [stdEdit, setStdEdit] = useState({ stock_mizuho: '', stock_shizai: '', change_reason: '' });
  const [stdHistory, setStdHistory] = useState([]);
  const [showStdHistory, setShowStdHistory] = useState(false);
  const [savingStd, setSavingStd] = useState(false);

  // 得意先コード表示用：基幹どおり（先頭0を保持して）表示する
  const formatCustomerDisp = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value).trim();
  };
  
  // 発送日を取得（分納計画がある場合は分納計画の発送日を優先）
  const getShipmentDate = (row) => {
    if (row.latest_shipment_date) {
      return row.latest_shipment_date;
    }
    return row.shipment_date;
  };

  const loadPurchaseOrders = async (filterType = shipmentDateFilter) => {
    if (loadingPurchaseOrders) {
      return;
    }
    
    try {
      setLoadingPurchaseOrders(true);
      const filters = {
        productDispExact: productDisp
      };
      
      // 発送日フィルタを適用
      if (filterType === 'null') {
        filters.shipmentDateNullOnly = true;
      } else if (filterType === 'not_null') {
        // バックエンドには「not_null」の直接的なパラメータがないため、
        // フロントエンドでフィルタリングするか、全件取得してフィルタリング
        // ここでは全件取得してフロントエンドでフィルタリングする方法を採用
      }
      
      const poData = await shippingApi.getShipmentPlan(100, 0, filters);
      
      // not_nullの場合はフロントエンドでフィルタリング
      let filteredData = poData;
      if (filterType === 'not_null') {
        filteredData = poData.filter(item => {
          const hasSplitPlans = item.split_plans && item.split_plans.length > 0;
          if (hasSplitPlans) {
            // 分納計画がある場合は、分納計画の発送日を確認
            return item.split_plans.some(plan => plan.shipment_date);
          } else {
            // 分納計画がない場合は、発注明細レベルの発送日を確認
            return item.latest_shipment_date || item.shipment_date;
          }
        });
      }
      
      // 発送日未設定の絞り込み後は、発注日が古い順にソート
      // バックエンドで既にフィルタリングされているため、フロントエンドではソートのみ実行
      if (filterType === 'null') {
        // 発注日文字列をパースするヘルパー関数
        // 形式: "24/01/15(月)" または "2024-01-15" など
        const parseHacchuDate = (dateStr) => {
          if (!dateStr || dateStr === '-') return null;
          
          // "24/01/15(月)" 形式の場合
          const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{2})/);
          if (match) {
            const year = 2000 + parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const day = parseInt(match[3], 10);
            return new Date(year, month, day);
          }
          
          // ISO形式やその他の形式を試す
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date;
          }
          
          return null;
        };
        
        // 発注日が古い順にソート
        filteredData.sort((a, b) => {
          const dateA = parseHacchuDate(a.hacchu_date);
          const dateB = parseHacchuDate(b.hacchu_date);
          
          // 両方nullの場合は順序を変えない
          if (!dateA && !dateB) return 0;
          // nullの場合は最後に配置
          if (!dateA) return 1;
          if (!dateB) return -1;
          
          return dateA - dateB; // 古い順（昇順）
        });
      }
      
      setPurchaseOrders(filteredData);
    } catch (error) {
      console.error('発送予定一覧取得エラー:', error);
      setPurchaseOrders([]);
    } finally {
      setLoadingPurchaseOrders(false);
    }
  };

  useEffect(() => {
    loadData();
    // productDispやfactoryが変更された時は、発送予定一覧の状態をリセット
    setPurchaseOrders([]);
    setShipmentDateFilter('all');
  }, [productDisp, factory]);

  useEffect(() => {
    // 発送予定一覧をデフォルトで読み込む（expandedSections.purchaseOrdersがtrueの場合）
    if (expandedSections.purchaseOrders) {
      loadPurchaseOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedSections.purchaseOrders, productDisp, factory, shipmentDateFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      // 必須データ（summary, calendar）のみを並列で取得
      const [summaryResult, calendarResult, stdResult] = await Promise.allSettled([
        inventoryApi.getSummary(productDisp, factory),
        inventoryApi.getCalendar(productDisp, factory),
        inventoryApi.getStandardStock(productDisp),
      ]);
      
      // 各結果を処理（エラー時はデフォルト値を設定）
      if (summaryResult.status === 'fulfilled') {
        setSummary(summaryResult.value);
      } else {
        console.error('在庫状況ヘッダー取得エラー:', summaryResult.reason);
      }
      
      if (calendarResult.status === 'fulfilled') {
        setCalendar(calendarResult.value);
      } else {
        console.error('カレンダー取得エラー:', calendarResult.reason);
        setCalendar({ days: [] });
      }

      if (stdResult.status === 'fulfilled') {
        setStdStock(stdResult.value);
        setStdEdit({
          stock_mizuho: String(stdResult.value.stock_mizuho ?? 0),
          stock_shizai: String(stdResult.value.stock_shizai ?? 0),
          change_reason: '',
        });
      }
    } catch (error) {
      console.error('在庫状況取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    const tasks = [loadData(), refreshSyncStatus()];
    if (expandedSections.purchaseOrders) {
      tasks.push(loadPurchaseOrders());
    }
    await Promise.all(tasks);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return Number(value).toLocaleString();
  };

  const formatWeight = (value) => {
    if (value === null || value === undefined) return '-';
    const n = Number(value);
    return Number.isFinite(n) ? `${n.toFixed(2)} kg` : '-';
  };

  const handleSaveStandardStock = async () => {
    setSavingStd(true);
    try {
      const res = await inventoryApi.updateStandardStock(productDisp, {
        stock_mizuho: parseInt(stdEdit.stock_mizuho, 10) || 0,
        stock_shizai: parseInt(stdEdit.stock_shizai, 10) || 0,
        change_reason: stdEdit.change_reason || undefined,
      });
      setStdStock(res);
      setStdEdit({
        stock_mizuho: String(res.stock_mizuho),
        stock_shizai: String(res.stock_shizai),
        change_reason: '',
      });
      if (summary) {
        setSummary({
          ...summary,
          standard_stock_total: res.standard_stock_total,
          standard_stock_mizuho: res.stock_mizuho,
          standard_stock_shizai: res.stock_shizai,
        });
      }
    } catch (e) {
      alert(e.message || '保存に失敗しました');
    } finally {
      setSavingStd(false);
    }
  };

  const handleShowStdHistory = async () => {
    try {
      const rows = await inventoryApi.getStandardStockHistory(productDisp);
      setStdHistory(rows);
      setShowStdHistory(true);
    } catch (e) {
      alert(e.message || '履歴の取得に失敗しました');
    }
  };

  const formatDateForCalendar = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    return `${date.getMonth() + 1}/${date.getDate()}(${weekdays[date.getDay()]})`;
  };

  // 月カレンダーを生成する関数
  const generateMonthCalendar = (targetMonth) => {
    const year = targetMonth.getFullYear();
    const month = targetMonth.getMonth();
    
    // 月の最初の日と最後の日
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // カレンダーに表示する最初の日（前月の日曜日から）
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());
    
    // カレンダーに表示する最後の日（次月の土曜日まで）
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));
    
    const days = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      // カレンダーデータから該当日の情報を取得
      const dayData = calendar?.days?.find(dayItem => {
        const dayDate = new Date(dayItem.date);
        return dayDate.toISOString().split('T')[0] === dateStr;
      });
      
      const isCurrentMonth = currentDate.getMonth() === month;
      const isToday = currentDate.toDateString() === new Date().toDateString();
      const dayOfWeek = currentDate.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      days.push({
        date: dateStr,
        dateObj: new Date(currentDate),
        isCurrentMonth,
        isToday,
        isWeekend,
        isHoliday: dayData?.is_holiday || false,
        inbound_qty: dayData?.inbound_qty || 0,
        outbound_qty: dayData?.outbound_qty || 0,
        stock_remaining: dayData?.stock_remaining || 0,
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  };

  const handlePrevMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCalendarMonth(new Date());
  };

  const formatDateInput = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '';
    return price.toLocaleString('ja-JP', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getRowId = (row) => {
    return `${row.hacchu_code}-${row.hacchu_changecnt}-${row.hacchu_datacode}`;
  };

  const handleRowClick = (row, e) => {
    // 「詳細」ボタンのクリックは展開をトリガーしない
    if (e && e.target.closest('.detail-button')) {
      return;
    }
    // 発注No列のクリックは展開をトリガーしない
    if (e && e.target.closest('button[class*="text-blue-600"]')) {
      return;
    }
    
    const rowId = getRowId(row);
    const newExpandedRows = new Set(expandedRows);
    
    if (newExpandedRows.has(rowId)) {
      // 既に開いている場合は閉じる
      newExpandedRows.delete(rowId);
    } else {
      // 新しい行を開く場合は、既存の展開をすべて閉じてから開く
      newExpandedRows.clear();
      newExpandedRows.add(rowId);
    }
    
    setExpandedRows(newExpandedRows);
  };

  // 発送先総重量を計算する共通関数
  const calculateShipmentDestinationTotalWeight = async (item) => {
    // デフォルトでその明細の総重量（製品の重量×発注数量）を設定
    const defaultWeight = item.total_weight || 0;
    
    // 発送日はlatest_shipment_dateを優先（分納計画がある場合は分納計画の最新発送日）
    const shipmentDate = item.latest_shipment_date || item.shipment_date;
    const nonyusaki = item.nonyusaki;
    
    // 発送日と納入先が設定されている場合、同じ発送日・納入先の他の明細を取得して加算
    if (shipmentDate && nonyusaki) {
      setLoadingTotalWeight(true);
      try {
        // 発送日をYYYY-MM-DD形式に変換
        let shipmentDateStr = null;
        if (shipmentDate instanceof Date) {
          shipmentDateStr = shipmentDate.toISOString().split('T')[0];
        } else if (typeof shipmentDate === 'string') {
          // 既にYYYY-MM-DD形式の場合はそのまま使用
          if (shipmentDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
            shipmentDateStr = shipmentDate;
          } else {
            // その他の形式の場合はformatDateを使用
            shipmentDateStr = formatDate(shipmentDate);
          }
        }
        
        if (shipmentDateStr) {
          // 同じ発送日・納入先の明細を取得（バックエンド limit 上限 1000 のためページング）
          let sameDestinationItems = [];
          let offset = 0;
          const limit = 1000;
          let hasMore = true;

          while (hasMore) {
            const batch = await shippingApi.getShipmentPlan(limit, offset, {
              shipment_date: shipmentDateStr,
              nonyusaki: nonyusaki,
            });

            if (batch && batch.length > 0) {
              sameDestinationItems = sameDestinationItems.concat(batch);
              offset += limit;
              hasMore = batch.length === limit;
            } else {
              hasMore = false;
            }
          }

          const currentItemId = `${item.hacchu_code}-${item.hacchu_changecnt}-${item.hacchu_datacode}`;
          const otherItemsWeight = sameDestinationItems
            .filter(otherItem => {
              const otherItemId = `${otherItem.hacchu_code}-${otherItem.hacchu_changecnt}-${otherItem.hacchu_datacode}`;
              const isDifferent = otherItemId !== currentItemId;

              const otherShipmentDate = otherItem.latest_shipment_date || otherItem.shipment_date;
              let otherShipmentDateStr = null;
              if (otherShipmentDate) {
                if (typeof otherShipmentDate === 'string' && otherShipmentDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                  otherShipmentDateStr = otherShipmentDate;
                } else if (otherShipmentDate instanceof Date) {
                  otherShipmentDateStr = otherShipmentDate.toISOString().split('T')[0];
                } else if (typeof otherShipmentDate === 'string') {
                  try {
                    const date = new Date(otherShipmentDate);
                    if (!isNaN(date.getTime())) {
                      otherShipmentDateStr = date.toISOString().split('T')[0];
                    }
                  } catch (e) {
                    // 日付のパースに失敗した場合はnullのまま
                  }
                }
              }
              const shipmentDateMatches = otherShipmentDateStr === shipmentDateStr;

              return isDifferent && shipmentDateMatches;
            })
            .reduce((sum, otherItem) => {
              return sum + (otherItem.total_weight || 0);
            }, 0);

          const totalWeight = defaultWeight + otherItemsWeight;
          setShipmentDestinationTotalWeight(totalWeight);
        } else {
          // 発送日が変換できない場合はデフォルト重量のみ
          setShipmentDestinationTotalWeight(defaultWeight);
        }
      } catch (error) {
        console.error('発送先総重量取得エラー:', error);
        // エラー時はデフォルト重量のみ
        setShipmentDestinationTotalWeight(defaultWeight);
      } finally {
        setLoadingTotalWeight(false);
      }
    } else {
      // 発送日または納入先が設定されていない場合はデフォルト重量のみ
      setShipmentDestinationTotalWeight(defaultWeight);
    }
  };

  const applyShipmentPlanRowPatch = useCallback((patch, row) => {
    const rowKey = getRowId(row);
    setPurchaseOrders((prevItems) =>
      prevItems.map((item) => {
        if (getRowId(item) !== rowKey) return item;
        const updated = { ...item, ...patch };
        if (
          patch.shipment_date !== undefined &&
          (!item.split_plans || item.split_plans.length === 0)
        ) {
          updated.latest_shipment_date = patch.shipment_date;
        }
        return updated;
      })
    );
    setSelectedItem((prev) => {
      if (!prev || getRowId(prev) !== rowKey) return prev;
      const updated = { ...prev, ...patch };
      if (
        patch.shipment_date !== undefined &&
        (!prev.split_plans || prev.split_plans.length === 0)
      ) {
        updated.latest_shipment_date = patch.shipment_date;
      }
      return updated;
    });
  }, []);

  const shipmentPlanFields = useShipmentPlanFieldSave({
    formatDate: formatShipmentPlanDate,
    onPatchRow: applyShipmentPlanRowPatch,
    onAfterShipmentDateSaved: calculateShipmentDestinationTotalWeight,
  });

  const handleDetailClick = async (row, e) => {
    if (e) e.stopPropagation();
    setSelectedItem(row);
    shipmentPlanFields.initFromRow(row);
    setDrawerOpen(true);
    await calculateShipmentDestinationTotalWeight(row);
  };

  const handleDateChange = async (index, value) => {
    const item = purchaseOrders[index];
    if (!item) return;
    
    try {
      // バックエンドに保存
      await shippingApi.updateShipmentPlan(
        item.hacchu_code,
        item.hacchu_changecnt,
        item.hacchu_datacode,
        { shipment_date: value || null }
      );
      
      // ローカル状態を更新
      const updatedItems = [...purchaseOrders];
      updatedItems[index].shipment_date = value || null;
      const hasSplitPlans = updatedItems[index].split_plans && updatedItems[index].split_plans.length > 0;
      const hasSplitPlanShipmentDate =
        hasSplitPlans && updatedItems[index].split_plans.some(plan => plan.shipment_date);
      if (!hasSplitPlanShipmentDate) {
        updatedItems[index].latest_shipment_date = value || null;
      }
      setPurchaseOrders(updatedItems);

      if (value) {
        const nextIndex = index + 1;
        if (nextIndex < purchaseOrders.length) {
          const nextItem = purchaseOrders[nextIndex];
          const nextHasSplitPlans = nextItem.split_plans && nextItem.split_plans.length > 0;
          const nextHasSplitPlanShipmentDate =
            nextHasSplitPlans && nextItem.split_plans.some(plan => plan.shipment_date);
          if (!nextHasSplitPlanShipmentDate) {
            const nextKey = `shipment_date-${nextItem.hacchu_code}-${nextItem.hacchu_changecnt}-${nextItem.hacchu_datacode}`;
            const focusNextShipmentDateInput = () => {
              const nextInput = inputRefs.current[nextKey];
              if (!nextInput) return false;
              nextInput.focus();
              if (nextInput.showPicker && typeof nextInput.showPicker === 'function') {
                try {
                  nextInput.showPicker();
                } catch (err) {
                  nextInput.click();
                }
              } else {
                nextInput.click();
              }
              return true;
            };
            setTimeout(() => {
              if (!focusNextShipmentDateInput()) {
                setTimeout(() => focusNextShipmentDateInput(), 200);
              }
            }, 250);
          }
        }
      }
    } catch (error) {
      console.error('発送日保存エラー:', error);
      alert('発送日の保存に失敗しました');
    }
  };

  const handleDateKeyDown = (e, index, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // 次のレコードの入力欄にフォーカス
      const nextIndex = index + 1;
      if (nextIndex < purchaseOrders.length) {
        const nextKey = `${field}-${purchaseOrders[nextIndex].hacchu_code}-${purchaseOrders[nextIndex].hacchu_changecnt}-${purchaseOrders[nextIndex].hacchu_datacode}`;
        setTimeout(() => {
          if (inputRefs.current[nextKey]) {
            inputRefs.current[nextKey].focus();
            inputRefs.current[nextKey].select();
          }
        }, 0);
      }
    }
  };

  const handleSplitPlanDataChange = (rowId, splitPlans) => {
    setPurchaseOrders((prevItems) =>
      patchItemsSplitPlans(prevItems, rowId, getRowId, splitPlans, ['latest_shipment_date'])
    );
  };

  const toggleSection = (section) => {
    const newExpanded = !expandedSections[section];
    setExpandedSections(prev => ({
      ...prev,
      [section]: newExpanded,
    }));
    
    // セクションが展開された時にデータを読み込む（遅延読み込み）
    if (newExpanded && section === 'purchaseOrders') {
      loadPurchaseOrders();
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-slate-600">読み込み中...</div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-slate-600">データが見つかりません</div>
      </div>
    );
  }

  const factoryName = factory === '26' ? '瑞穂倉庫' : '資材倉庫';

  return (
    <div className="h-full flex flex-col bg-slate-50 overflow-y-auto">
      {/* ヘッダー：製品情報 */}
      <div className="p-6 border-b border-slate-200 bg-white compact:px-4 compact:py-3">
        <div className="page-header mb-4">
          <h1 className="page-title">在庫状況</h1>
          <div className="page-header-actions">
            <PageSyncMeta groups={['inventory', 'orders']} />
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 btn-toolbar bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              更新
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-sm text-slate-600">製品コード</div>
            <div className="text-lg font-medium text-slate-900">{summary.product_disp}</div>
          </div>
          <div>
            <div className="text-sm text-slate-600">製品名</div>
            <div className="text-lg font-medium text-slate-900">{summary.product_name}</div>
          </div>
          <div>
            <div className="text-sm text-slate-600">寸法</div>
            <div className="text-lg font-medium text-slate-900">{summary.size_text || '-'}</div>
          </div>
          <div>
            <div className="text-sm text-slate-600">倉庫</div>
            <div className="text-lg font-medium text-slate-900">{factoryName}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div>
            <div className="text-sm text-slate-600">現在庫</div>
            <div className="text-lg font-medium text-slate-900">{formatNumber(summary.stock_current)}</div>
          </div>
          <div>
            <div className="text-sm text-slate-600">発注残</div>
            <div className="text-lg font-medium text-slate-900">{formatNumber(summary.po_remaining)}</div>
          </div>
          <div>
            <div className="text-sm text-slate-600">受注残</div>
            <div className="text-lg font-medium text-slate-900">{formatNumber(summary.so_remaining)}</div>
          </div>
        </div>
        <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-800">標準在庫（編集）</h3>
            <button
              type="button"
              onClick={handleShowStdHistory}
              className="text-sm text-blue-600 underline"
            >
              変更履歴
            </button>
          </div>
          <div className="text-sm text-slate-600 mb-2">
            全体: {formatNumber(stdStock?.standard_stock_total ?? summary?.standard_stock_total ?? 0)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-xs text-slate-600 mb-1">瑞穂倉庫</label>
              <input
                type="number"
                min="0"
                value={stdEdit.stock_mizuho}
                onChange={(e) => setStdEdit({ ...stdEdit, stock_mizuho: e.target.value })}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">資材倉庫</label>
              <input
                type="number"
                min="0"
                value={stdEdit.stock_shizai}
                onChange={(e) => setStdEdit({ ...stdEdit, stock_shizai: e.target.value })}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1">変更理由（任意）</label>
              <input
                type="text"
                value={stdEdit.change_reason}
                onChange={(e) => setStdEdit({ ...stdEdit, change_reason: e.target.value })}
                className="w-full border rounded px-2 py-1"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleSaveStandardStock}
            disabled={savingStd}
            className="mt-3 px-4 py-2 bg-slate-800 text-white text-sm rounded hover:bg-slate-700 disabled:opacity-50"
          >
            {savingStd ? '保存中...' : '標準在庫を保存'}
          </button>
        </div>
        {showStdHistory && (
          <div className="mt-4 p-4 border border-slate-200 rounded-lg bg-white max-h-64 overflow-y-auto">
            <div className="flex justify-between mb-2">
              <h4 className="font-medium text-slate-800">変更履歴</h4>
              <button type="button" onClick={() => setShowStdHistory(false)} className="text-sm text-slate-500">
                閉じる
              </button>
            </div>
            {stdHistory.length === 0 ? (
              <p className="text-sm text-slate-500">履歴がありません</p>
            ) : (
              <ul className="text-sm space-y-2">
                {stdHistory.map((h) => (
                  <li key={h.history_id} className="border-b pb-1">
                    {formatDateTime(h.changed_at)} — {h.field_label || h.field_name}: {h.old_value} → {h.new_value}
                    {h.changed_by && <span className="text-slate-500"> ({h.changed_by})</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* セクションA：発送予定一覧 */}
      <div className="p-6 bg-white border-b border-slate-200 compact:px-4 compact:py-3">
        <div className="page-header mb-4">
          <button
            onClick={() => toggleSection('purchaseOrders')}
            className="flex items-center justify-between text-left flex-1 min-w-0"
          >
            <h2 className="text-xl font-semibold text-slate-900 compact:text-lg">発送予定一覧</h2>
            <svg
              className={`w-5 h-5 text-slate-600 transform transition-transform ml-2 ${
                expandedSections.purchaseOrders ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {expandedSections.purchaseOrders && (
            <div className="flex gap-2 ml-4">
              <button
                onClick={() => {
                  // トグル動作：'null'の場合は'all'に、それ以外は'null'に
                  const newFilter = shipmentDateFilter === 'null' ? 'all' : 'null';
                  setShipmentDateFilter(newFilter);
                  setPurchaseOrders([]);
                }}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  shipmentDateFilter === 'null'
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                発送日未設定
              </button>
            </div>
          )}
        </div>
        {expandedSections.purchaseOrders && (
          <p className="text-xs text-slate-500 mb-2">{SHIPMENT_ROW_LEGEND}</p>
        )}
        {expandedSections.purchaseOrders && (
          <div className="overflow-x-auto">
            {loadingPurchaseOrders ? (
              <div className="text-center py-8 text-slate-500">読み込み中...</div>
            ) : purchaseOrders.length === 0 ? (
              <div className="text-center py-8 text-slate-500">データがありません</div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-slate-200">
                <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
                  <table className="divide-y divide-slate-200" style={{ minWidth: 'max-content', width: '100%' }}>
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50">操作</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50">受注No/発注No</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50">得意先/納入先</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50">製品CD</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50">製品名/寸法</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50">発送数量</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50">発送日</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50">発注残数</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50">メモ</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {purchaseOrders.map((row, rowIdx) => {
                      const rowId = getRowId(row);
                      const isExpanded = expandedRows.has(rowId);
                      const rowStage = getShipmentRowStage(row.compflag, row.w_check);
                      return (
                        <React.Fragment key={rowId}>
                          <tr
                            onClick={(e) => handleRowClick(row, e)}
                            className={`cursor-pointer transition-colors ${getShipmentRowClassName(rowStage)}`}
                          >
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                              <button
                                onClick={(e) => handleDetailClick(row, e)}
                                className="detail-button px-3 py-1 bg-slate-600 text-white text-xs rounded hover:bg-slate-700 transition-colors"
                              >
                                詳細
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-900 whitespace-normal align-top max-w-[360px]">
                              <div className="leading-snug">
                                <div className="truncate">{row.juchu_no || '-'}</div>
                                <div className="text-slate-600 truncate flex items-center gap-2">
                                  <span>{row.hacchu_no || '-'}</span>
                                  {row.split_plans && row.split_plans.length > 0 && (
                                    <span
                                      className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full"
                                      title="分納あり"
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                      </svg>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-900 whitespace-normal align-top max-w-[360px]">
                              <div className="leading-snug">
                                <div className="truncate">{row.customer_name || '-'}</div>
                                <div className="text-slate-600 truncate">{row.nonyusaki || '-'}</div>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">{row.product_disp || '-'}</td>
                            <td className="px-4 py-3 text-sm text-slate-900 whitespace-normal align-top max-w-[360px]">
                              <div className="leading-snug">
                                <div className="truncate">{row.product_name || '-'}</div>
                                <div className="text-slate-600 truncate">{row.product_size || '-'}</div>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                              {row.hacchu_amount == null ? '-' : `${formatNumber(row.hacchu_amount)}${row.tani ? ` ${row.tani}` : ''}`}
                            </td>
                            <td className={`px-4 py-3 whitespace-nowrap text-sm text-slate-900 ${
                              isShipmentDateUnset(row) ? getShipmentDateUnsetCellClassName(true) : ''
                            }`}>
                              {(() => {
                                const hasSplitPlans = row.split_plans && row.split_plans.length > 0;
                                // 分納計画に発送日が設定されているかどうかを確認
                                const hasSplitPlanShipmentDate = hasSplitPlans && 
                                  row.split_plans.some(plan => plan.shipment_date);
                                
                                if (hasSplitPlanShipmentDate) {
                                  // 分納計画に発送日が設定されている場合は表示のみ（最新の日付を表示）
                                  const latestShipmentDate = row.split_plans.reduce((latest, plan) => {
                                    if (!plan.shipment_date) return latest;
                                    const planDate = new Date(plan.shipment_date);
                                    if (!latest || planDate > latest) return planDate;
                                    return latest;
                                  }, null);
                                  const displayDate = latestShipmentDate ? latestShipmentDate.toISOString().split('T')[0] : (row.latest_shipment_date || row.shipment_date);
                                  return <span className="text-slate-900">{formatDate(displayDate) || '-'}</span>;
                                }
                                
                                // 分納計画がない、または分納計画に発送日がない場合は編集可能
                                const displayDate = row.latest_shipment_date || row.shipment_date;
                                const key = `shipment_date-${row.hacchu_code}-${row.hacchu_changecnt}-${row.hacchu_datacode}`;
                                return (
                                  <input
                                    ref={(el) => {
                                      if (el) inputRefs.current[key] = el;
                                    }}
                                    type="date"
                                    value={formatDate(displayDate) || ''}
                                    onChange={(e) => handleDateChange(rowIdx, e.target.value)}
                                    onKeyDown={(e) => handleDateKeyDown(e, rowIdx, 'shipment_date')}
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.stopPropagation()}
                                    className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-500 text-slate-900"
                                    style={{ minWidth: '120px' }}
                                  />
                                );
                              })()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                              {row.hacchu_zan_amount !== null && row.hacchu_zan_amount !== undefined ? formatNumber(row.hacchu_zan_amount) : '-'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                              <ShipmentMemoBadges row={row} />
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={9} className="p-0 bg-slate-50">
                                <div className="border-t border-slate-200">
                                  <HacchuSplitPlanInlineEditor
                                    hacchuCode={row.hacchu_code}
                                    hacchuChangecnt={row.hacchu_changecnt}
                                    hacchuDatacode={String(row.hacchu_datacode)}
                                    hacchuAmount={row.hacchu_amount}
                                    onDataChange={(splitPlans) => handleSplitPlanDataChange(rowId, splitPlans)}
                                    hideSupplierResponseDueDate={true}
                                    initialSplitPlans={row.split_plans ?? []}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* セクションB：入出荷カレンダー */}
      <div className="p-6 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => toggleSection('calendar')}
            className="flex items-center justify-between text-left flex-1"
          >
            <h2 className="text-xl font-semibold text-slate-900">入出荷カレンダー</h2>
            <svg
              className={`w-5 h-5 text-slate-600 transform transition-transform ml-2 ${
                expandedSections.calendar ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {expandedSections.calendar && (
            <div className="flex gap-2 ml-4">
              <button
                onClick={() => setCalendarViewType('week')}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  calendarViewType === 'week'
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                週
              </button>
              <button
                onClick={() => setCalendarViewType('month')}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  calendarViewType === 'month'
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                月
              </button>
            </div>
          )}
        </div>
        {expandedSections.calendar && calendar && calendar.days && calendar.days.length > 0 ? (
          <>
            {/* 月送りボタン群（月ビューのみ表示） */}
            {calendarViewType === 'month' && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <button
                  onClick={handlePrevMonth}
                  className="px-3 py-1 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors"
                >
                  ← 前月
                </button>
                <button
                  onClick={handleToday}
                  className="px-3 py-1 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors"
                >
                  今日
                </button>
                <span className="text-lg font-semibold text-slate-900 min-w-[120px] text-center">
                  {calendarMonth.getFullYear()}年{calendarMonth.getMonth() + 1}月
                </span>
                <button
                  onClick={handleNextMonth}
                  className="px-3 py-1 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors"
                >
                  次月 →
                </button>
              </div>
            )}

            {calendarViewType === 'week' ? (
              <div className="overflow-x-auto">
                <div className="min-w-full">
                  {/* 週ビュー：カレンダー本体 */}
                  <div className="grid grid-cols-7 gap-1">
                    {calendar.days.map((day, idx) => {
                      const date = new Date(day.date);
                      const isToday = date.toDateString() === new Date().toDateString();
                      const isWeekend = day.is_weekend;
                      const isHoliday = day.is_holiday;
                      // 78日後は最後のセル（49番目 = 7列×7行の最後）
                      const isSpecial = idx === calendar.days.length - 1 && calendar.days.length === 49;
                      
                      // 日付文字列のフォーマット（MM/DD形式）
                      const dateStr = formatDateForCalendar(day.date);
                      
                      return (
                        <div
                          key={idx}
                          className={`p-1.5 border rounded min-h-[80px] flex flex-col ${
                            isToday
                              ? 'bg-blue-50 border-blue-400 shadow-sm'
                              : isHoliday || isWeekend
                              ? 'bg-red-50 border-red-200'
                              : 'bg-white border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          {/* 日付 */}
                          <div className={`text-xs font-bold mb-1 pb-0.5 border-b ${
                            isToday 
                              ? 'text-blue-700 border-blue-300' 
                              : isHoliday || isWeekend 
                              ? 'text-red-700 border-red-200' 
                              : 'text-slate-700 border-slate-200'
                          }`}>
                            <div className="leading-tight">{dateStr}</div>
                            {isToday && (
                              <span className="text-[10px] text-blue-600 font-normal">今日</span>
                            )}
                            {isSpecial && (
                              <span className="text-[10px] text-orange-600 font-normal">78日後</span>
                            )}
                          </div>
                          
                          {/* 入荷数 */}
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[10px] text-slate-500">入</span>
                            <span className={`text-xs font-semibold leading-tight ${
                              day.inbound_qty > 0 ? 'text-green-600' : 'text-slate-400'
                            }`}>
                              {formatNumber(day.inbound_qty)}
                            </span>
                          </div>
                          
                          {/* 出荷数 */}
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[10px] text-slate-500">出</span>
                            <span className={`text-xs font-semibold leading-tight ${
                              day.outbound_qty > 0 ? 'text-red-600' : 'text-slate-400'
                            }`}>
                              {formatNumber(day.outbound_qty)}
                            </span>
                          </div>
                          
                          {/* 在庫残 - 大きく強調 */}
                          <div className="flex items-center justify-between mt-auto pt-0.5 border-t-2 border-slate-300">
                            <span className="text-[10px] font-medium text-slate-600">残</span>
                            <span className="text-base font-bold text-slate-900 leading-tight">
                              {formatNumber(day.stock_remaining)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-full">
                  {/* 月ビュー：カレンダー本体 */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {/* 曜日ヘッダー */}
                    {['日', '月', '火', '水', '木', '金', '土'].map((day, idx) => (
                      <div
                        key={idx}
                        className={`p-2 text-center text-xs font-semibold ${
                          idx === 0 ? 'text-red-600' : idx === 6 ? 'text-blue-600' : 'text-slate-700'
                        }`}
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {generateMonthCalendar(calendarMonth).map((day, idx) => {
                      const dateObj = day.dateObj;
                      const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
                      const dateStr = `${dateObj.getMonth() + 1}/${dateObj.getDate()}(${weekdays[dateObj.getDay()]})`;
                      
                      return (
                        <div
                          key={idx}
                          className={`p-1.5 border rounded min-h-[80px] flex flex-col ${
                            !day.isCurrentMonth
                              ? 'bg-slate-50 border-slate-100 opacity-50'
                              : day.isToday
                              ? 'bg-blue-50 border-blue-400 shadow-sm'
                              : day.isHoliday || day.isWeekend
                              ? 'bg-red-50 border-red-200'
                              : 'bg-white border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          {/* 日付 */}
                          <div className={`text-xs font-bold mb-1 pb-0.5 border-b ${
                            !day.isCurrentMonth
                              ? 'text-slate-400 border-slate-200'
                              : day.isToday 
                              ? 'text-blue-700 border-blue-300' 
                              : day.isHoliday || day.isWeekend 
                              ? 'text-red-700 border-red-200' 
                              : 'text-slate-700 border-slate-200'
                          }`}>
                            <div className="leading-tight">{dateStr}</div>
                            {day.isToday && (
                              <span className="text-[10px] text-blue-600 font-normal">今日</span>
                            )}
                          </div>
                          
                          {/* 入荷数 */}
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[10px] text-slate-500">入</span>
                            <span className={`text-xs font-semibold leading-tight ${
                              day.inbound_qty > 0 ? 'text-green-600' : 'text-slate-400'
                            }`}>
                              {formatNumber(day.inbound_qty)}
                            </span>
                          </div>
                          
                          {/* 出荷数 */}
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[10px] text-slate-500">出</span>
                            <span className={`text-xs font-semibold leading-tight ${
                              day.outbound_qty > 0 ? 'text-red-600' : 'text-slate-400'
                            }`}>
                              {formatNumber(day.outbound_qty)}
                            </span>
                          </div>
                          
                          {/* 在庫残 - 大きく強調 */}
                          <div className="flex items-center justify-between mt-auto pt-0.5 border-t-2 border-slate-300">
                            <span className="text-[10px] font-medium text-slate-600">残</span>
                            <span className="text-base font-bold text-slate-900 leading-tight">
                              {formatNumber(day.stock_remaining)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : expandedSections.calendar && calendar && calendar.days && calendar.days.length === 0 ? (
          <div className="text-center text-slate-500 py-8">
            カレンダーデータがありません
          </div>
        ) : expandedSections.calendar && !calendar ? (
          <div className="text-center text-slate-500 py-8">
            カレンダーデータの読み込みに失敗しました
          </div>
        ) : null}
      </div>
      <DetailDrawer
        isOpen={drawerOpen}
        onClose={async () => {
          await shipmentPlanFields.handleDrawerClose();
          setDrawerOpen(false);
        }}
        title={selectedItem ? `${selectedItem.hacchu_no} - ${selectedItem.product_name}` : '詳細'}
      >
        {selectedItem && (
          <div className="space-y-4">
                {/* 基本情報カード */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <h3 className="font-semibold text-lg mb-3 text-slate-900">基本情報</h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">仕入先CD</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.supplier_disp}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">仕入先名</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.supplier_name}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">資材担当</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.hacchu_shain_name || '-'}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">営業担当</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.tanto_shain_name || '-'}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">発注日</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.hacchu_date || '-'}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">資材発注No</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.hacchu_no}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">発注訂正回数</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.hacchu_changecnt ?? 0}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">製品コード</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.product_disp || '-'}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">製品名</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.product_name}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">寸法</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.product_size || '-'}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">発注数量</span>
                      <span className="text-slate-900 flex-1 break-words">{formatNumber(selectedItem.hacchu_amount)} {selectedItem.tani || ''}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">発注残数</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.hacchu_zan_amount !== null && selectedItem.hacchu_zan_amount !== undefined ? formatNumber(selectedItem.hacchu_zan_amount) : '-'} {selectedItem.tani || ''}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">仕入単価</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.stanka !== null && selectedItem.stanka !== undefined ? formatPrice(selectedItem.stanka) : '-'}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">出荷形態</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.shukka_type || '-'}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">受注No</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.juchu_no || '-'}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">依頼No</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.hcirai_no || '-'}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">希望納期</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.nouki || '-'}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">得意先</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.customer_name || '-'}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">納入先名</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.nonyusaki || '-'}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">納入先住所</span>
                      <span className="text-slate-900 flex-1 break-words">{selectedItem.direct_address || '-'}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">発送日</span>
                      <span className="text-slate-900 flex-1 break-words">{formatDate(selectedItem.shipment_date) || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* 調整・発送情報カード */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <h3 className="font-semibold text-lg mb-3 text-slate-900">調整・発送情報</h3>
                  <ShipmentPlanDetailFields
                    row={selectedItem}
                    draft={shipmentPlanFields.draft}
                    onDraftChange={shipmentPlanFields.updateDraftField}
                    onFieldBlur={shipmentPlanFields.saveFieldOnBlur}
                    loadingTotalWeight={loadingTotalWeight}
                    shipmentDestinationTotalWeight={shipmentDestinationTotalWeight}
                  />
                </div>
          </div>
        )}
      </DetailDrawer>

    </div>
  );
};

export default InventoryDetailPage;
